import axios from 'axios';

let cachedPermissions: string[] | null = null;
let permissionsPromise: Promise<string[]> | null = null;
let cachedVerificationData: any | null = null;
let verificationPromise: Promise<any> | null = null;

async function getVerificationData(): Promise<any> {
  if (cachedVerificationData !== null) {
    return cachedVerificationData;
  }

  if (verificationPromise !== null) {
    return verificationPromise;
  }

  verificationPromise = (async () => {
    const baseURL =
      process.env.NODE_ENV === 'development'
        ? 'http://localhost:4000'
        : 'https://api.rummacare.com';

    const resp = await axios.get(`${baseURL}/api/authentication/verificar`, {
      withCredentials: true,
    });

    const verificationData = resp?.data || {};
    cachedVerificationData = verificationData;

    return verificationData;
  })();

  try {
    return await verificationPromise;
  } finally {
    verificationPromise = null;
  }
}

/**
 * Verifica los permisos del usuario desde el backend.
 * Los resultados se cachean para evitar multiples llamadas.
 */
export async function getUserPermissions(): Promise<string[]> {
  if (cachedPermissions !== null) {
    return cachedPermissions;
  }

  if (permissionsPromise !== null) {
    return permissionsPromise;
  }

  permissionsPromise = (async () => {
    try {
      const verificationData = await getVerificationData();
      const raw = verificationData?.functions;
      const functions: string[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : [];

      cachedPermissions = functions;
      return functions;
    } catch (err) {
      console.error('Error verificando permisos:', err);
      return [];
    } finally {
      permissionsPromise = null;
    }
  })();

  return permissionsPromise;
}

/**
 * Returns logged user id from verification payload.
 * Maps fd_usuarios_pk from /api/authentication/verificar.
 */
export async function getLoggedUserId(): Promise<string | null> {
  try {
    const verificationData = await getVerificationData();
    const userId = verificationData?.data?.fd_usuarios_pk;

    if (userId === undefined || userId === null || userId === '') {
      return null;
    }

    return String(userId);
  } catch (err) {
    console.error('Error obteniendo fd_usuarios_pk:', err);
    return null;
  }
}

/**
 * Returns whether Rumma Cardio is enabled for the logged user.
 * This is a per-user flag, not a profile permission.
 */
export async function getLoggedUserCardEnabled(): Promise<boolean> {
  try {
    const verificationData = await getVerificationData();
    return verificationData?.data?.card_enabled === true;
  } catch (err) {
    console.error('Error obteniendo card_enabled:', err);
    return false;
  }
}

/**
 * Returns logged user tenant id from verification payload.
 */
export async function getLoggedUserTenantId(): Promise<string | null> {
  try {
    const verificationData = await getVerificationData();
    const tenantId = verificationData?.data?.tenant_id;

    if (tenantId === undefined || tenantId === null || tenantId === '') {
      return null;
    }

    return String(tenantId);
  } catch (err) {
    console.error('Error obteniendo tenant_id:', err);
    return null;
  }
}

/**
 * Returns associated_tenants from verification payload.
 */
export async function getLoggedUserAssociatedTenants(): Promise<string[]> {
  try {
    const verificationData = await getVerificationData();
    const associatedTenants = verificationData?.data?.associated_tenants;

    if (!Array.isArray(associatedTenants)) {
      return [];
    }

    return associatedTenants.map(tenantId => String(tenantId));
  } catch (err) {
    console.error('Error obteniendo associated_tenants:', err);
    return [];
  }
}

/**
 * Returns tenant access data (tenant_id + associated_tenants) from verification payload.
 */
export async function getLoggedUserTenantAccessData(): Promise<{
  tenantId: string | null;
  associatedTenants: string[];
}> {
  try {
    const verificationData = await getVerificationData();
    const tenantIdRaw = verificationData?.data?.tenant_id;
    const associatedTenantsRaw = verificationData?.data?.associated_tenants;

    return {
      tenantId:
        tenantIdRaw === undefined || tenantIdRaw === null || tenantIdRaw === ''
          ? null
          : String(tenantIdRaw),
      associatedTenants: Array.isArray(associatedTenantsRaw)
        ? associatedTenantsRaw.map(tenantId => String(tenantId))
        : [],
    };
  } catch (err) {
    console.error('Error obteniendo tenant access data:', err);
    return {
      tenantId: null,
      associatedTenants: [],
    };
  }
}

/**
 * Verifica si el usuario tiene un permiso especifico.
 */
export async function hasPermission(permission: string): Promise<boolean> {
  const permissions = await getUserPermissions();
  return permissions.includes(permission);
}

/**
 * Verifica si el usuario tiene alguno de los permisos especificados.
 */
export async function hasAnyPermission(permissions: string[]): Promise<boolean> {
  const userPermissions = await getUserPermissions();
  return permissions.some(permission => userPermissions.includes(permission));
}

/**
 * Verifica si el usuario tiene todos los permisos especificados.
 */
export async function hasAllPermissions(permissions: string[]): Promise<boolean> {
  const userPermissions = await getUserPermissions();
  return permissions.every(permission => userPermissions.includes(permission));
}

/**
 * Limpia el cache de permisos (util despues de login/logout).
 */
export function clearPermissionsCache(): void {
  cachedPermissions = null;
  permissionsPromise = null;
  cachedVerificationData = null;
  verificationPromise = null;
}
